import { formatDistanceToNow } from 'date-fns';

export const formatCount = (value: number | string): string => {
  if (typeof value === 'number') {
    return value.toLocaleString('en-US');
  }

  // Counts arrive from the summary as BIGINT strings — format via BigInt so
  // huge values keep their exact digits and still get thousands separators.
  try {
    return BigInt(value).toLocaleString('en-US');
  } catch {
    return value;
  }
};

export const formatRelativeTime = (isoDate: string): string => {
  const date = new Date(isoDate);

  if (Number.isNaN(date.getTime())) {
    return isoDate;
  }

  return formatDistanceToNow(date, { addSuffix: true });
};

export const formatProgress = (
  completedBatches: number,
  totalBatches: number,
): number => {
  if (!totalBatches) {
    return 0;
  }

  return Math.round((completedBatches / totalBatches) * 100);
};

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
/** `1024n` is unavailable: this package targets ES6, where BigInt literals are a syntax error. */
const BYTES_PER_UNIT = BigInt(1024);
const ZERO = BigInt(0);
const TEN = BigInt(10);

const toBigIntOrNull = (value: string | bigint): bigint | null => {
  try {
    return typeof value === 'bigint' ? value : BigInt(value);
  } catch {
    return null;
  }
};

/**
 * Formats a byte count as a human-readable size (`1536` → `1.5 KB`).
 *
 * Byte offsets arrive from PDGS as BIGINT strings, so every step of the
 * conversion stays in `BigInt` — a file large enough to exceed
 * `Number.MAX_SAFE_INTEGER` would silently lose digits if parsed as a
 * `number` first. The fractional part is derived from the division
 * remainder rather than from floating-point maths for the same reason.
 *
 * Returns the input untouched when it is not a valid integer string, which
 * mirrors `formatCount`'s behaviour for unparseable wire values.
 */
export const formatBytes = (value: string | bigint): string => {
  const bytes = toBigIntOrNull(value);

  if (bytes === null || bytes < ZERO) {
    return String(value);
  }

  let unitIndex = 0;
  let divisor = BigInt(1);

  // Step up a unit only when the value fully fills the next one, so 1023
  // stays `1023 B` and 1024 becomes `1 KB`.
  while (
    unitIndex < BYTE_UNITS.length - 1 &&
    bytes / (divisor * BYTES_PER_UNIT) > ZERO
  ) {
    divisor *= BYTES_PER_UNIT;
    unitIndex += 1;
  }

  const whole = bytes / divisor;
  const tenths = ((bytes % divisor) * TEN) / divisor;
  // Whole bytes are never fractional, and a trailing `.0` is just noise.
  const fraction = unitIndex > 0 && tenths > ZERO ? `.${tenths}` : '';

  return `${whole.toLocaleString('en-US')}${fraction} ${BYTE_UNITS[unitIndex]}`;
};

/**
 * Formats a batch's byte range as `{start} – {end} ({size})`, e.g.
 * `0 B – 52.4 MB (52.4 MB)`.
 *
 * Both offsets are BIGINT strings. When either is unparseable the raw values
 * are shown and the size is omitted, so an unexpected payload degrades to
 * something still readable instead of `NaN`.
 */
export const formatByteRange = (start: string, end: string): string => {
  const startBytes = toBigIntOrNull(start);
  const endBytes = toBigIntOrNull(end);

  if (startBytes === null || endBytes === null) {
    return `${start} – ${end}`;
  }

  // PDGS always emits end >= start; clamp rather than render a negative size
  // if that ever stops being true.
  const size = endBytes > startBytes ? endBytes - startBytes : ZERO;

  return `${formatBytes(startBytes)} – ${formatBytes(endBytes)} (${formatBytes(
    size,
  )})`;
};

/**
 * Turns a SCREAMING_SNAKE wire value into a readable label
 * (`GATEKEEPER_ASSESSMENT` → `Gatekeeper Assessment`).
 *
 * Used for pipeline stage names, which the BFF deliberately exposes as open
 * strings rather than a GraphQL enum, so there is no curated label map to
 * look them up in.
 */
export const formatWireLabel = (value: string): string =>
  value
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');

/** Default number of leading characters kept by {@link truncateId}. */
export const DEFAULT_TRUNCATED_ID_LENGTH = 8;

/**
 * Shortens an opaque identifier (record UUID, batch id) for display in a
 * dense table. The full value is still available — callers pair this with a
 * tooltip or a copy control rather than dropping it.
 */
export const truncateId = (
  id: string,
  length: number = DEFAULT_TRUNCATED_ID_LENGTH,
): string => (id.length <= length ? id : `${id.slice(0, length)}…`);
